// @flow

import { all, call, put, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";

import http from "@http";
import serviceActionCreator from "@modules/service/actionCreator";
import {
  CreateNurseTransferOffer,
  GetNurseTransferOfferSchema,
  GetAvailableNurseAdvisors,
  AssignEmployeeToNurseAdvisor,
  DeassignEmployeeToNurseAdvisor,
  GetIncomingNurseTransfer,
  AcceptNurseTransfer,
} from "@modules/recording/constants/actions";
import actionCreator from "@modules/recording/actionCreator";
import type { Advisor } from "@modules/management/types";
import type { NurseTransferOfferType, NurseTransferQueueType } from "@modules/recording/types";
import { parseSchema } from "@utils/schema";
import { sendSuccessMessage } from "@modules/app/actionCreator";

const delay = ms => new Promise(res => setTimeout(res, ms));

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type CreateNurseTransferOfferRequest = $Exact<{
  data: NurseTransferOfferType,
  clientId: number,
  callInteractionId: number,
  nurseTransferOfferId: number | null,
}>;

export function* onCreateNurseTransferOffer({
  payload,
}: Action<CreateNurseTransferOfferRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.data || !payload.clientId || !payload.callInteractionId) {
      throw Error("Invalid payload provided");
    }

    if (payload.nurseTransferOfferId) {
      yield call(
        http.post,
        `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/nurse-transfer-offer/${payload.nurseTransferOfferId}/cancel`,
      );
    }

    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/nurse-transfer-offer`,
      payload.data,
    );

    yield put(actionCreator.CreateNurseTransferOfferSuccess(response.data));
    yield put(
      sendSuccessMessage(
        payload.data.outcome === "DECLINE" ? "NURSE_TRANSFER_DECLINED" : "NURSE_TRANSFER_ACCEPTED",
      ),
    );
  } catch (e) {
    yield put(actionCreator.CreateNurseTransferOfferError((e || {}).response));
  }
}

type GetNurseTransferOfferSchemaRequest = $Exact<{
  clientId: number,
}>;

export function* onGetNurseTransferOfferSchema({
  payload,
}: Action<GetNurseTransferOfferSchemaRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield parseSchema("NurseTransferOfferDTO", { clientId: payload.clientId });

    yield put(actionCreator.GetNurseTransferOfferSchemaSuccess(response));
  } catch (e) {
    yield put(actionCreator.GetNurseTransferOfferSchemaError((e || {}).response));
  }
}

type GetAvailableNurseAdvisorsRequest = $Exact<{
  name: string,
}>;

function* onGetAvailableNurseAdvisors({
  payload,
}: Action<GetAvailableNurseAdvisorsRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<Advisor>> },
> {
  try {
    if (!payload || !payload.name) {
      throw new Error("Invalid payload");
    }

    const USER_TYPES = ["NURSE_ADVISOR", "LEAD_NURSE_ADVISOR", "CONTACT_CENTRE_MANAGER"];
    const response = yield call(http.get, `/api/v1/system-management/nurse-user`, {
      params: {
        limit: 100,
        search: `last_name~${payload.name},is_active=true${USER_TYPES.reduce(
          (acc: string, u: string) => `${acc},user_type:${u}`,
          "",
        )}`,
      },
    });

    yield put(actionCreator.GetAvailableNurseAdvisorsSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetAvailableNurseAdvisorsError((e || {}).response));
  }
}

type AssignEmployeeToNurseAdvisorRequest = $Exact<{
  clientId: number,
  callInteractionId: number,
  nurseId: number,
}>;

function* onAssignEmployeeToNurseAdvisor({
  payload,
}: Action<AssignEmployeeToNurseAdvisorRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.callInteractionId || !payload.nurseId) {
      throw new Error("Invalid payload");
    }

    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/nurse-transfer-offer`,
      {
        outcome: "ACCEPT",
        nurse_transfer_user_id: payload.nurseId,
      },
    );

    yield put(actionCreator.AssignEmployeeToNurseAdvisorSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.AssignEmployeeToNurseAdvisorError((e || {}).response));
  }
}

type DeassignEmployeeToNurseAdvisorRequest = $Exact<{
  clientId: number,
  callInteractionId: number,
  nurseTransferOfferId: number,
}>;

function* onDeassignEmployeeToNurseAdvisor({
  payload,
}: Action<DeassignEmployeeToNurseAdvisorRequest>): Generator<*, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.callInteractionId ||
      !payload.nurseTransferOfferId
    ) {
      throw new Error("Invalid payload");
    }

    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/nurse-transfer-offer/${payload.nurseTransferOfferId}/cancel`,
    );

    yield put(actionCreator.DeassignEmployeeToNurseAdvisorSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.DeassignEmployeeToNurseAdvisorError((e || {}).response));
  }
}

export function* onGetIncomingNurseTransfer(): Generator<*, void, *> {
  try {
    const response = yield call(
      http.get,
      `/api/v1/contact-centre/nurse-transfer-queue-for-logged-in-user`,
    );

    yield put(actionCreator.GetIncomingNurseTransferSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetIncomingNurseTransferError((e || {}).response));
  }
}

type AcceptNurseTransferRequest = $Exact<{
  transfer: NurseTransferQueueType,
}>;

export function* onAcceptNurseTransfer({
  payload,
}: Action<AcceptNurseTransferRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.transfer) {
      throw Error("Invalid payload provided");
    }

    const { transfer } = payload;
    const nurseTransferOfferId: number = transfer.id;
    const clientId: number = transfer.absence.clientId;
    const employeeId: number = transfer.employee.id;
    const callerIdentity: string = "SELF";
    const reasonForCall: string = "START_UPDATE_CLOSE_ABSENCE";

    const settingsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${clientId}/service-config/platform/data-security-employee-identity`,
    );
    const clientResponse = yield call(
      http.get,
      `/api/v1/contact-centre/client/${clientId}/absence-recording-configuration`,
    );

    const employeeResponse = yield call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}`,
    );
    const employeeSchema = yield parseSchema("EmployeeDTO", { clientId });

    yield put(actionCreator.GetEmployeeAbsenceRecording({ clientId, employeeId }));

    yield put(serviceActionCreator.GetServiceSettings({ clientId }));

    yield call(delay, 500);

    yield put(
      actionCreator.CreateCallInteraction({
        clientId,
        employeeId,
        formData: {
          employeeId,
          callerIdentity,
          reasonForCall,
          interactionSource: "TRANSFER",
          nurseTransferOfferId,
        },
      }),
    );

    const response = yield call(
      http.post,
      `/api/v1/contact-centre/nurse-transfer-offer/${nurseTransferOfferId}/incoming-nurse-transfer/accept`,
    );

    yield all([
      put(actionCreator.SetClient(clientResponse.data)),
      put(actionCreator.SetSettings(settingsResponse.data)),
      put(actionCreator.SetEmployee(employeeResponse.data)),
      put(actionCreator.SetEmployeeSchema(employeeSchema)),
      put(actionCreator.AcceptNurseTransferSuccess(response.data)),
      put(actionCreator.ToggleNurseTransferModal(true)),
    ]);
  } catch (e) {
    yield put(actionCreator.AcceptNurseTransferError((e || {}).response));
  }
}

export function* NurseTransferSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetNurseTransferOfferSchema, onGetNurseTransferOfferSchema),
    takeEvery(CreateNurseTransferOffer, onCreateNurseTransferOffer),
    takeEvery(GetAvailableNurseAdvisors, onGetAvailableNurseAdvisors),
    takeEvery(AssignEmployeeToNurseAdvisor, onAssignEmployeeToNurseAdvisor),
    takeEvery(DeassignEmployeeToNurseAdvisor, onDeassignEmployeeToNurseAdvisor),
    takeEvery(GetIncomingNurseTransfer, onGetIncomingNurseTransfer),
    takeEvery(AcceptNurseTransfer, onAcceptNurseTransfer),
  ]);
}

export default NurseTransferSaga;
